import {
  PublicClientApplication,
  Configuration,
  AuthenticationResult,
  SilentRequest,
  RedirectRequest,
  EndSessionRequest,
  AccountInfo
} from '@azure/msal-browser';
import { AzureAuthConfig, AuthUser, AzureAuthError } from '../types/auth';
import { env } from '../env';

// Module-level variables for singleton pattern
let msalInstance: PublicClientApplication | null = null;
let config: AzureAuthConfig | null = null;

const createAuthConfig = (): AzureAuthConfig => {
  return {
    clientId: env.REACT_APP_AZURE_APPLICATION_ID || '',
    authority: `https://login.microsoftonline.com/${
      env.REACT_APP_AZURE_TENANT_ID || 'common'
    }`,
    redirectUri: `${window.location.origin}/auth/callback`,
    postLogoutRedirectUri: `${window.location.origin}/login`,
    scopes: ['openid', 'profile', 'email', 'User.Read']
  };
};

const createMsalConfig = (authConfig: AzureAuthConfig): Configuration => {
  return {
    auth: {
      clientId: authConfig.clientId,
      authority: authConfig.authority,
      redirectUri: authConfig.redirectUri,
      postLogoutRedirectUri: authConfig.postLogoutRedirectUri,
      navigateToLoginRequestUrl: false
    },
    cache: {
      cacheLocation: 'localStorage',
      storeAuthStateInCookie: false
    },
    system: {
      loggerOptions: {
        loggerCallback: (level, message, containsPii) => {
          if (!containsPii) {
            // Uncomment the next line to enable logging
            // This is useful for debugging but should be disabled in production
            // to avoid logging sensitive information
            // console.log(`MSAL [${level}]: ${message}`);
          }
        }
      }
    }
  };
};

const initializeAuthService = (): void => {
  if (!config) {
    config = createAuthConfig();
  }
  if (!msalInstance) {
    msalInstance = new PublicClientApplication(createMsalConfig(config));
  }
};

const getMsalInstance = (): PublicClientApplication => {
  if (!msalInstance) {
    initializeAuthService();
  }
  return msalInstance!;
};

const getConfig = (): AzureAuthConfig => {
  if (!config) {
    initializeAuthService();
  }
  return config!;
};

export const initialize = async (): Promise<void> => {
  try {
    const msal = getMsalInstance();
    await msal.initialize();

    // Check if this is a redirect callback
    if (isRedirectCallback()) {
      await handleRedirectCallback();
    }
  } catch (error) {
    console.error('Failed to initialize MSAL:', error);
    throw createAuthError(
      'INITIALIZATION_FAILED',
      'Failed to initialize Azure authentication'
    );
  }
};

export const isRedirectCallback = (): boolean => {
  const currentUrl = window.location.pathname;
  return (
    currentUrl === '/auth/callback' ||
    window.location.search.includes('code=') ||
    window.location.search.includes('error=')
  );
};

export const loginRedirect = async (): Promise<void> => {
  try {
    const authConfig = getConfig();
    const loginRequest: RedirectRequest = {
      scopes: authConfig.scopes,
      redirectUri: authConfig.redirectUri
    };

    const msal = getMsalInstance();
    await msal.loginRedirect(loginRequest);
  } catch (error) {
    console.error('Login redirect failed:', error);
    throw createAuthError(
      'LOGIN_REDIRECT_FAILED',
      'Failed to initiate login redirect'
    );
  }
};

export const handleRedirectCallback =
  async (): Promise<AuthenticationResult | null> => {
    try {
      const msal = getMsalInstance();
      const response = await msal.handleRedirectPromise();

      if (response && response.account) {
        msal.setActiveAccount(response.account);
        return response;
      }

      return null;
    } catch (error) {
      console.error('Handle redirect callback failed:', error);
      throw createAuthError(
        'CALLBACK_FAILED',
        'Failed to process authentication callback'
      );
    }
  };

export const getAccessToken = async (): Promise<string | null> => {
  try {
    const account = getActiveAccount();
    if (!account) {
      return null;
    }

    const authConfig = getConfig();
    const silentRequest: SilentRequest = {
      scopes: authConfig.scopes,
      account: account
    };

    const msal = getMsalInstance();
    const response = await msal.acquireTokenSilent(silentRequest);
    return response.accessToken;
  } catch (error) {
    console.error('Failed to acquire token silently:', error);

    // If silent token acquisition fails, try to acquire token with redirect
    try {
      const authConfig = getConfig();
      const loginRequest: RedirectRequest = {
        scopes: authConfig.scopes,
        account: getActiveAccount() || undefined
      };

      const msal = getMsalInstance();
      await msal.acquireTokenRedirect(loginRequest);
      return null; // Token will be available after redirect
    } catch (redirectError) {
      console.error('Failed to acquire token with redirect:', redirectError);
      throw createAuthError(
        'TOKEN_ACQUISITION_FAILED',
        'Failed to acquire access token'
      );
    }
  }
};

export const logout = async (): Promise<void> => {
  try {
    const account = getActiveAccount();
    const authConfig = getConfig();

    const logoutRequest: EndSessionRequest = {
      account: account,
      postLogoutRedirectUri: authConfig.postLogoutRedirectUri
    };

    const msal = getMsalInstance();
    await msal.logoutRedirect(logoutRequest);
  } catch (error) {
    console.error('Logout failed:', error);
    throw createAuthError('LOGOUT_FAILED', 'Failed to logout');
  }
};

export const getActiveAccount = (): AccountInfo | null => {
  const msal = getMsalInstance();
  return msal.getActiveAccount();
};

export const getAllAccounts = (): AccountInfo[] => {
  const msal = getMsalInstance();
  return msal.getAllAccounts();
};

export const isAuthenticated = (): boolean => {
  const account = getActiveAccount();
  return account !== null;
};

export const getUserInfo = (): AuthUser | null => {
  const account = getActiveAccount();
  if (!account) {
    return null;
  }

  return {
    id: account.localAccountId,
    email: account.username,
    name: account.name || '',
    firstName: (account.idTokenClaims?.given_name as string) || '',
    lastName: (account.idTokenClaims?.family_name as string) || '',
    roles: account.idTokenClaims?.roles || []
  };
};

const createAuthError = (code: string, message: string): AzureAuthError => {
  return {
    errorCode: code,
    errorMessage: message
  };
};

export const clearCache = (): void => {
  // MSAL automatically manages cache clearing during logout
  // For manual clearing, we can set active account to null
  const msal = getMsalInstance();
  msal.setActiveAccount(null);
};

export const acquireTokenSilent = (
  request: SilentRequest
): Promise<AuthenticationResult> => {
  const msal = getMsalInstance();
  return msal.acquireTokenSilent(request);
};

export const acquireTokenPopup = (request: {
  scopes: string[];
}): Promise<AuthenticationResult> => {
  const msal = getMsalInstance();
  return msal.acquireTokenPopup(request);
};

// Default export for backward compatibility
const azureAuthService = {
  initialize,
  isRedirectCallback,
  loginRedirect,
  handleRedirectCallback,
  getAccessToken,
  logout,
  getActiveAccount,
  getAllAccounts,
  isAuthenticated,
  getUserInfo,
  clearCache,
  acquireTokenSilent,
  acquireTokenPopup
};

export default azureAuthService;
